import { CheckCircle, CloudQueue, Done, Error, HelpOutline, Schedule, Send, Timer } from '@mui/icons-material';
import { Chip, ChipProps } from '@mui/material';
import React from 'react';
import { ExecutionStatus } from '../../types/commands';

interface ExecutionStatusBadgeProps {
  status: ExecutionStatus;
  size?: 'small' | 'medium';
}

interface StatusConfig {
  label: string;
  color: ChipProps['color'];
  icon: React.ReactElement;
  bgcolor: string;
}

const statusConfig: Record<ExecutionStatus, StatusConfig> = {
  pending: {
    label: 'Pending',
    color: 'default',
    icon: <Schedule fontSize="small" />,
    bgcolor: '#9e9e9e',
  },
  queued: {
    label: 'Queued',
    color: 'default',
    icon: <CloudQueue fontSize="small" />,
    bgcolor: '#7e57c2',
  },
  sent: {
    label: 'Sent',
    color: 'info',
    icon: <Send fontSize="small" />,
    bgcolor: '#2196f3',
  },
  acknowledged: {
    label: 'Acknowledged',
    color: 'info',
    icon: <CheckCircle fontSize="small" />,
    bgcolor: '#03a9f4',
  },
  completed: {
    label: 'Completed',
    color: 'success',
    icon: <Done fontSize="small" />,
    bgcolor: '#4caf50',
  },
  failed: {
    label: 'Failed',
    color: 'error',
    icon: <Error fontSize="small" />,
    bgcolor: '#f44336',
  },
  timeout: {
    label: 'Timeout',
    color: 'warning',
    icon: <Timer fontSize="small" />,
    bgcolor: '#ff9800',
  },
};

/**
 * Turn an unrecognised status value into something readable, e.g. `half_sent` → `Half sent`.
 */
const humanizeStatus = (status: string): string => {
  const words = status.replace(/[_-]+/g, ' ').trim();

  if (!words) {
    return 'Unknown';
  }

  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Look up the chip configuration for an execution status.
 *
 * Accepts `string` on purpose: the status comes from the API, so the backend can introduce
 * a value this build has never heard of (as it did with `queued`). An unknown status
 * degrades to a neutral chip instead of crashing the surrounding list.
 */
export const getExecutionStatusConfig = (status: string): StatusConfig => {
  return (
    statusConfig[status as ExecutionStatus] ?? {
      label: humanizeStatus(status ?? ''),
      color: 'default',
      icon: <HelpOutline fontSize="small" />,
      bgcolor: '#9e9e9e',
    }
  );
};

export const ExecutionStatusBadge: React.FC<ExecutionStatusBadgeProps> = ({ status, size = 'small' }) => {
  const config = getExecutionStatusConfig(status);

  return (
    <Chip
      label={config.label}
      color={config.color}
      size={size}
      icon={config.icon}
      sx={{
        'fontWeight': 600,
        'backgroundColor': config.bgcolor,
        'color': 'white',
        '& .MuiChip-icon': {
          color: 'white',
        },
      }}
    />
  );
};
